import type { RateLimitInfo } from "@medplum/core";
import * as Sentry from "@sentry/nextjs";
import { extractFetchUrlPath } from "@/lib/medplum/extract-fetch-url-path";
import { shouldInitSentry } from "@/lib/sentry/should-init-sentry";

const GAUGE_NAME = "medplum.rate_limit.fhir_interactions.remaining";

// Medplum rate limits are quite layered.
// To start with FhirInteractions are calculated on a point based system per resource and action.
// Then, different audiences have different quotas, which you can find in the UserConfiguration table in
// medplum.
const LOW_REMAINING_UNITS_FHIR = 20_000;
// Requests then have their own rate limit. Usually this is about
// 60,000 requests per IP per minute. However, for auth endpoints,
// this value becomes 160 per IP per minute. And for login and newuser/project endpoints, this is only 5.
const LOW_REMAINING_UNITS_REQUESTS = 1_000;
const LOW_REMAINING_UNITS_AUTH_REQUESTS = 100;
const LOW_REMAINING_UNITS_AUTH_UI_REQUESTS = 2;

export interface CallWithSentrySpanInput<TResult> {
  name: string;
  callback: (span?: any) => TResult | Promise<TResult>;
}

export function callWithSentrySpan<TResult>({
  name,
  callback,
}: CallWithSentrySpanInput<TResult>): TResult | Promise<TResult> {
  if (!shouldInitSentry()) {
    return callback(undefined);
  }

  const startSpan: unknown = (Sentry as unknown as { startSpan?: unknown }).startSpan;
  if (typeof startSpan !== "function") {
    return callback(undefined);
  }

  return (startSpan as any)({ name, op: "http.client" }, (span: any) => callback(span));
}

/** Web app login/new user/project flows get an extremely tight per-IP request quota. */
function isThrottledAuthRequestPath(urlPath: string): boolean {
  if (urlPath === "") {
    return false;
  }
  const path = urlPath.trim().split("?")[0] ?? "";
  return path === "/auth/login" || path === "/auth/newuser" || path === "/auth/newproject";
}

/** Medplum auth routes use a much tighter per-IP request quota than FHIR traffic. */
function isAuthRequestPath(urlPath: string): boolean {
  if (urlPath === "") {
    return false;
  }
  const path = urlPath.trim().split("?")[0] ?? "";
  return (
    path === "/auth" ||
    path.startsWith("/auth/") ||
    path === "/oauth2" ||
    path.startsWith("/oauth2/")
  );
}

function getLowRemainingUnitsThreshold(limitName: string, urlPath: string): number {
  if (limitName === "fhirInteractions") {
    return LOW_REMAINING_UNITS_FHIR;
  }

  if (limitName === "requests" && isThrottledAuthRequestPath(urlPath)) {
    return LOW_REMAINING_UNITS_AUTH_UI_REQUESTS;
  }

  if (limitName === "requests" && isAuthRequestPath(urlPath)) {
    return LOW_REMAINING_UNITS_AUTH_REQUESTS;
  }

  if (limitName === "requests") {
    return LOW_REMAINING_UNITS_REQUESTS;
  }

  // Unknown limit names: treat like the requests bucket.
  return 1000;
}

function shouldCaptureLowRemainingUnits({
  limitName,
  response,
  urlPath,
}: {
  limitName: string;
  response: Response;
  urlPath: string;
}): boolean {
  if (limitName === "requests" && isThrottledAuthRequestPath(urlPath) && response.status < 400) {
    // Successful login calls often leave Medplum's tiny per-IP auth bucket low. The gauge/span
    // retain that capacity signal; Sentry should remain reserved for actual throttling/failures.
    return false;
  }

  return true;
}

function parseRateLimitHeader(header: string): RateLimitInfo[] {
  const nowSeconds = Math.ceil(Date.now() / 1000);

  return [...header.matchAll(/"([^"]+)";r=(\d+);t=(\d+)/g)].map(([, name, r, t]) => ({
    name,
    remainingUnits: Number(r),
    secondsUntilReset: Number(t),
    resetsAfter: nowSeconds + Number(t),
  }));
}

interface RateLimitReportContext {
  label?: string;
  url?: string;
  spanOverride?: any;
}

export function reportRateLimitStatusFromResponse(
  response: Response,
  { label, url, spanOverride }: RateLimitReportContext = {},
): void {
  if (!shouldInitSentry()) {
    return;
  }
  try {
    const header = response.headers.get("RateLimit");
    if (!header) {
      return;
    }

    const urlPath = url != null && url !== "" ? extractFetchUrlPath(url) : "";
    const limits = parseRateLimitHeader(header);

    if (spanOverride) {
      spanOverride.setAttribute?.("medplum.rateLimit.header", header);
    }

    for (const limit of limits) {
      Sentry.metrics?.gauge(GAUGE_NAME, limit.remainingUnits, {
        attributes: {
          limitName: limit.name,
          isThrottledAuthRequestPath: isThrottledAuthRequestPath(urlPath),
          isAuthRequestPath: isAuthRequestPath(urlPath),
          ...(label != null ? { label } : {}),
        },
      });

      const lowRemainingUnitsThreshold = getLowRemainingUnitsThreshold(limit.name, urlPath);
      if (
        limit.remainingUnits < lowRemainingUnitsThreshold &&
        shouldCaptureLowRemainingUnits({ limitName: limit.name, response, urlPath })
      ) {
        const trimmedUrlPath = urlPath.trim();
        const extraParams = trimmedUrlPath !== "" ? { urlPath: trimmedUrlPath } : {};

        Sentry.captureMessage("Medplum rate limit low", {
          level: "warning",
          tags: {
            limitName: limit.name,
            ...(label != null ? { label } : {}),
          },
          extra: {
            rateLimitHeader: header,
            remainingUnits: limit.remainingUnits,
            secondsUntilReset: limit.secondsUntilReset,
            resetsAfter: limit.resetsAfter,
            ...extraParams,
          },
        });
      }
    }
  } catch (error) {
    // no op
  }
}
